<script setup>
import { ref, computed, toRef } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();
const query = ref('');
const selectedCategory = ref('all');
const isRefreshing = ref(false);

// Threshold for marketing template warning (conservative value)
// Meta's actual limit is dynamic per user, so we warn early
const MARKETING_TEMPLATE_WARNING_THRESHOLD = 2;

const categoryOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utilidade' },
  { value: 'AUTHENTICATION', label: 'Autenticação' },
];

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const filteredTemplateMessages = computed(() =>
  whatsAppTemplateMessages.value.filter(template => {
    const matchesQuery = template.name
      .toLowerCase()
      .includes(query.value.toLowerCase());
    const matchesCategory =
      selectedCategory.value === 'all' ||
      template.category === selectedCategory.value;
    return matchesQuery && matchesCategory;
  })
);

// Count WhatsApp template messages sent since the last incoming (user) message.
// WhatsApp templates sent by agents are stored as:
//   - message_type: OUTGOING (1) with additional_attributes.template_params
// Note: message_type TEMPLATE (3) is for system templates (CSAT, greeting, etc.) — not WhatsApp templates
const marketingTemplatesSinceLastReply = computed(() => {
  if (!props.conversationId) return 0;

  const conversation = store.getters.getConversationById(props.conversationId);
  const messages = conversation?.messages || [];
  if (!messages.length) return 0;

  // Find the index of the last incoming message (user reply)
  let lastIncomingIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].message_type === MESSAGE_TYPE.INCOMING) {
      lastIncomingIndex = i;
      break;
    }
  }

  // Count outgoing template messages after the last incoming message
  let count = 0;
  const startIndex = lastIncomingIndex + 1;
  for (let i = startIndex; i < messages.length; i += 1) {
    const msg = messages[i];
    if (msg.message_type !== MESSAGE_TYPE.OUTGOING) {
      // eslint-disable-next-line no-continue
      continue;
    }
    // WhatsApp templates have template_params in additional_attributes
    const additionalAttrs = msg.additional_attributes || {};
    if (additionalAttrs.template_params) {
      count += 1;
    }
  }

  return count;
});

// Show warning when marketing templates threshold is reached
const showMarketingLimitWarning = computed(
  () =>
    marketingTemplatesSinceLastReply.value >=
    MARKETING_TEMPLATE_WARNING_THRESHOLD
);

const getTemplateBody = template => {
  return findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
};

const getTemplateHeader = template => {
  return findComponentByType(template, COMPONENT_TYPES.HEADER);
};

const getTemplateFooter = template => {
  return findComponentByType(template, COMPONENT_TYPES.FOOTER);
};

const getTemplateButtons = template => {
  return findComponentByType(template, COMPONENT_TYPES.BUTTONS);
};

const hasMediaContent = template => {
  const header = getTemplateHeader(template);
  return header && MEDIA_FORMATS.includes(header.format);
};

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
</script>

<template>
  <div class="w-full">
    <!-- Marketing template limit warning banner -->
    <div
      v-if="showMarketingLimitWarning"
      class="flex items-start gap-2 p-3 mb-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200"
    >
      <Icon icon="i-lucide-alert-triangle" class="size-4 shrink-0 mt-0.5" />
      <div class="text-xs leading-relaxed">
        <p class="font-medium mb-1">
          {{ t('WHATSAPP_TEMPLATES.PICKER.MARKETING_LIMIT.TITLE') }}
        </p>
        <p>
          {{
            t('WHATSAPP_TEMPLATES.PICKER.MARKETING_LIMIT.DESCRIPTION', {
              count: marketingTemplatesSinceLastReply,
            })
          }}
        </p>
        <a
          href="https://developers.facebook.com/docs/whatsapp/messaging-limits"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 mt-1.5 text-amber-700 dark:text-amber-300 underline hover:no-underline font-medium"
        >
          {{ t('WHATSAPP_TEMPLATES.PICKER.MARKETING_LIMIT.LEARN_MORE') }}
          <Icon icon="i-lucide-external-link" class="size-3" />
        </a>
      </div>
    </div>
    <div class="flex gap-2 mb-2.5">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-12" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <select
        v-model="selectedCategory"
        class="shrink-0 h-9 w-auto max-w-[7.5rem] px-2 rounded-lg bg-n-alpha-black2 text-n-slate-12 text-xs outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand cursor-pointer"
      >
        <option
          v-for="opt in categoryOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
      <button
        :disabled="isRefreshing"
        class="flex justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 disabled:opacity-50 disabled:cursor-not-allowed"
        :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        @click="refreshTemplates"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
      </button>
    </div>
    <div
      class="bg-n-background outline-n-container outline outline-1 rounded-lg max-h-[21.5rem] md:max-h-[37.5rem] overflow-y-auto p-2.5"
    >
      <div v-for="(template, i) in filteredTemplateMessages" :key="template.id">
        <button
          class="block p-2.5 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-2 dark:hover:bg-n-solid-2"
          @click="emit('onSelect', template)"
        >
          <div>
            <div class="flex justify-between items-center mb-2.5">
              <p class="text-sm">
                {{ template.name }}
              </p>
              <span
                class="inline-block px-2 py-1 text-xs leading-none rounded-lg cursor-default bg-n-slate-3 text-n-slate-12"
              >
                {{ t('WHATSAPP_TEMPLATES.PICKER.LABELS.LANGUAGE') }}:
                {{ template.language }}
              </span>
            </div>
            <!-- Header -->
            <div v-if="getTemplateHeader(template)" class="mb-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('WHATSAPP_TEMPLATES.PICKER.HEADER') || 'HEADER' }}
              </p>
              <div
                v-if="getTemplateHeader(template).format === 'TEXT'"
                class="text-sm label-body"
              >
                {{ getTemplateHeader(template).text }}
              </div>
              <div
                v-else-if="hasMediaContent(template)"
                class="text-sm italic text-n-slate-11"
              >
                {{
                  t('WHATSAPP_TEMPLATES.PICKER.MEDIA_CONTENT', {
                    format: getTemplateHeader(template).format,
                  }) ||
                  `${getTemplateHeader(template).format} ${t('WHATSAPP_TEMPLATES.PICKER.MEDIA_CONTENT_FALLBACK')}`
                }}
              </div>
            </div>

            <!-- Body -->
            <div>
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('WHATSAPP_TEMPLATES.PICKER.BODY') || 'BODY' }}
              </p>
              <p class="text-sm label-body">{{ getTemplateBody(template) }}</p>
            </div>

            <!-- Footer -->
            <div v-if="getTemplateFooter(template)" class="mt-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('WHATSAPP_TEMPLATES.PICKER.FOOTER') || 'FOOTER' }}
              </p>
              <p class="text-sm label-body">
                {{ getTemplateFooter(template).text }}
              </p>
            </div>

            <!-- Buttons -->
            <div v-if="getTemplateButtons(template)" class="mt-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('WHATSAPP_TEMPLATES.PICKER.BUTTONS') || 'BUTTONS' }}
              </p>
              <div class="flex flex-wrap gap-1 mt-1">
                <span
                  v-for="button in getTemplateButtons(template).buttons"
                  :key="button.text"
                  class="px-2 py-1 text-xs rounded bg-n-slate-3 text-n-slate-12"
                >
                  {{ button.text }}
                </span>
              </div>
            </div>

            <div class="mt-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('WHATSAPP_TEMPLATES.PICKER.CATEGORY') || 'CATEGORY' }}
              </p>
              <p class="text-sm">{{ template.category }}</p>
            </div>
          </div>
        </button>
        <hr
          v-if="i != filteredTemplateMessages.length - 1"
          :key="`hr-${i}`"
          class="border-b border-solid border-n-weak my-2.5 mx-auto max-w-[95%]"
        />
      </div>
      <div v-if="!filteredTemplateMessages.length" class="py-8 text-center">
        <div
          v-if="
            (query || selectedCategory !== 'all') &&
            whatsAppTemplateMessages.length
          "
        >
          <p>
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
            <strong>{{ query }}</strong>
          </p>
        </div>
        <div v-else-if="!whatsAppTemplateMessages.length" class="space-y-4">
          <p class="text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.label-body {
  font-family: monospace;
}
</style>
